import type { ApiSuccess, Milestone, UpdateMilestoneInput } from '@just-us/shared'
/**
 * hooks/use-milestones.ts
 *
 * TanStack Query hooks for milestones:
 * - useMilestones()        — list all milestones ordered by date ASC
 * - useCreateMilestone()   — POST /milestones (multipart/form-data)
 * - useUpdateMilestone()   — PATCH /milestones/:id (JSON)
 * - useDeleteMilestone()   — DELETE /milestones/:id
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '../lib/api-client'
import { getToken } from '../store/auth'

const API_BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:8787'

export const MILESTONES_QUERY_KEY = ['milestones'] as const

// ---------------------------------------------------------------------------
// useMilestones
// ---------------------------------------------------------------------------

export const useMilestones = () =>
  useQuery({
    queryKey: MILESTONES_QUERY_KEY,
    queryFn: () => apiFetch<{ data: Milestone[]; total: number }>('/milestones'),
    staleTime: 30_000,
  })

// ---------------------------------------------------------------------------
// useCreateMilestone — multipart/form-data
// Uses raw fetch (NOT apiFetch) so the browser sets the correct Content-Type
// boundary for multipart.
// ---------------------------------------------------------------------------

export const useCreateMilestone = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (formData: FormData): Promise<Milestone> => {
      const token = getToken()

      const res = await fetch(`${API_BASE}/milestones`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      })

      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: res.statusText }))
        throw new Error((body as { error?: string }).error ?? `HTTP ${res.status}`)
      }

      const json = (await res.json()) as { data: Milestone }
      return json.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MILESTONES_QUERY_KEY })
    },
  })
}

// ---------------------------------------------------------------------------
// useUpdateMilestone
// ---------------------------------------------------------------------------

export const useUpdateMilestone = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateMilestoneInput }) =>
      apiFetch<ApiSuccess<Milestone>>(`/milestones/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MILESTONES_QUERY_KEY })
    },
  })
}

// ---------------------------------------------------------------------------
// useDeleteMilestone
// ---------------------------------------------------------------------------

export const useDeleteMilestone = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<ApiSuccess<{ deleted: boolean }>>(`/milestones/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MILESTONES_QUERY_KEY })
    },
  })
}

// ---------------------------------------------------------------------------
// useAddMilestonePhotos — POST /milestones/:id/photos (multipart/form-data)
// ---------------------------------------------------------------------------

export const useAddMilestonePhotos = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      formData,
    }: { id: string; formData: FormData }): Promise<{ added: number }> => {
      const token = getToken()
      const res = await fetch(`${API_BASE}/milestones/${id}/photos`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: res.statusText }))
        throw new Error((body as { error?: string }).error ?? `HTTP ${res.status}`)
      }
      const json = (await res.json()) as { data: { added: number } }
      return json.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MILESTONES_QUERY_KEY })
    },
  })
}

// ---------------------------------------------------------------------------
// useRemoveMilestonePhoto — DELETE /milestones/:id/photos/:photoId
// ---------------------------------------------------------------------------

export const useRemoveMilestonePhoto = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ milestoneId, photoId }: { milestoneId: string; photoId: string }) =>
      apiFetch<ApiSuccess<{ deleted: boolean }>>(`/milestones/${milestoneId}/photos/${photoId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MILESTONES_QUERY_KEY })
    },
  })
}
